type RulesetLike = {
  id?: string;
  groupName?: string | null;
  name?: string | null;
  provider?: { name?: string | null } | null;
  payer?: { name?: string | null } | null;
  isDefault?: boolean;
};

export type RulesetOption = {
  label: string;
  value: string;
  searchText: string;
  isDefault: boolean;
};

export type RulesetOptionGroup = {
  label: string;
  items: RulesetOption[];
};

/**
 * Builds grouped MultiSelect options for a ruleset list on the contract
 * create/edit pages. Default rules go into the first group; provider/payer-
 * specific rules go into the second. Pair with `rulesetGroupTemplate` via the
 * MultiSelect's `optionGroupLabel`, `optionGroupChildren`, and
 * `optionGroupTemplate` props so PrimeReact renders a proper section header.
 *
 * @param nameField The ruleset field that holds a user-facing name. MPDR / Case
 *   Rate / Packages / Exploding Fee Schedule use `groupName`; Inpatient uses
 *   `name`.
 */
export function buildRulesetOptionGroups(
  rulesets: RulesetLike[] | undefined,
  nameField: "groupName" | "name",
  unnamedFallback: string
): RulesetOptionGroup[] {
  if (!rulesets) return [];

  const defaults: RulesetOption[] = [];
  const scoped: RulesetOption[] = [];

  rulesets
    .filter((r): r is RulesetLike & { id: string } => typeof r.id === "string")
    .forEach((ruleset) => {
      const displayName =
        (nameField === "groupName" ? ruleset.groupName : ruleset.name) ||
        unnamedFallback;
      const isDefault = !!ruleset.isDefault;
      const scope = isDefault
        ? "Default"
        : `${ruleset.provider?.name ?? "—"} - ${ruleset.payer?.name ?? "—"}`;
      const option: RulesetOption = {
        label: `${displayName} (${scope})`,
        value: ruleset.id,
        searchText: `${displayName} ${ruleset.provider?.name ?? ""} ${
          ruleset.payer?.name ?? ""
        } ${isDefault ? "default" : ""}`.toLowerCase(),
        isDefault,
      };
      (isDefault ? defaults : scoped).push(option);
    });

  const byLabel = (a: RulesetOption, b: RulesetOption) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase());
  defaults.sort(byLabel);
  scoped.sort(byLabel);

  const groups: RulesetOptionGroup[] = [];
  if (defaults.length > 0) {
    groups.push({ label: "Default rules", items: defaults });
  }
  if (scoped.length > 0) {
    groups.push({ label: "Provider/payer-specific rules", items: scoped });
  }
  return groups;
}

/**
 * Group-header template for the MultiSelect. Uses styling consistent with other
 * PrimeReact section headers (uppercase small caps, muted color) so it looks at
 * home in the existing UI.
 */
export function rulesetGroupTemplate(group: RulesetOptionGroup | undefined) {
  if (!group) return null;
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "#6b7280",
        background: "#f9fafb",
        padding: "0.5rem 0.75rem",
        borderTop: "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      {group.label}
    </div>
  );
}
